import { io } from "socket.io-client";
import { toast } from "react-hot-toast";

let socketInstance = null;

// Joins the appropriate rooms based on user role
export const joinUserRoom = (user) => {
  if (!socketInstance || !socketInstance.connected) {
    toast.error("Unable to join chat. Please try again later.");
    return;
  }

  if (!user || !user.id || !user.role) {
    toast.error("Cannot join chat due to an error. Please contact support.");
    return;
  }

  const parsedUserId = parseInt(user.id, 10);
  if (isNaN(parsedUserId) || parsedUserId <= 0) {
    toast.error("Cannot join chat due to an invalid user ID.");
    return;
  }

  const validRoles = ["admin", "writer", "student"];
  if (!validRoles.includes(user.role)) {
    toast.error("Cannot join chat due to an invalid user role.");
    return;
  }

  // Join user-specific room
  socketInstance.emit("join-room", { room: `user_${parsedUserId}` });

  // Role-specific room joins
  if (user.role === "admin") {
    socketInstance.emit("join-admin", { userId: parsedUserId, role: "admin" });
  }
};

export const initSocketClient = () => {
  if (socketInstance) return socketInstance;

  const SOCKET_URL = process.env.NODE_ENV === "production"
    ? process.env.NEXT_PUBLIC_WEBSOCKET_URL || "https://essaylite-websocket.onrender.com"
    : "http://localhost:10000";

  socketInstance = io(SOCKET_URL, {
    transports: ["websocket"],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
    autoConnect: false,
  });

  // Handle connection success silently (no toast)
  socketInstance.on("connect", () => {
    // No user-facing notification; connection is handled internally
  });

  // Handle connection errors with user-friendly message
  socketInstance.on("connect_error", (err) => {
    toast.error("Unable to connect to chat. Please try again later.");
  });

  // Handle reconnection failures with user-friendly message
  socketInstance.on("reconnect_failed", () => {
    toast.error("Chat connection lost. Please refresh the page.");
  });

  // Handle disconnect with user-friendly message
  socketInstance.on("disconnect", (reason) => {
    if (reason === "io server disconnect") {
      toast.error("Chat disconnected. Please try again.");
    }
  });

  // Handle server errors with user-friendly message
  socketInstance.on("error", (data) => {
    toast.error("An error occurred with the chat service. Please try again.");
  });

  // Handle notifications
  socketInstance.on("notification", (notification) => {
    const requiredFields = ["id", "title", "message", "userId", "role"];
    const isValid = requiredFields.every((field) => notification[field] !== undefined) &&
                   typeof notification.title === "string" &&
                   typeof notification.message === "string" &&
                   typeof notification.userId === "number" &&
                   ["admin", "writer", "student"].includes(notification.role);

    if (!isValid) {
      toast.error("Received an invalid chat notification.");
      return;
    }

    toast.success(`${notification.title}: ${notification.message}`, {
      duration: 5000,
      position: "top-right",
    });
  });

  // Connect the socket
  socketInstance.connect();

  return socketInstance;
};

export const disconnectSocket = () => {
  if (socketInstance && socketInstance.connected) {
    socketInstance.disconnect();
    socketInstance = null;
  }
};